/**!*******************************************************************
 *
 * Copyright (c) Epic AI GmbH. All rights reserved.
 * Author:  Alexander Claas
 *
 ********************************************************************!*/

import { fetchCloudRun } from "../Helper";
import { IGroupResult, IUserResult } from "@chatcaptain/types/organization";
import { useStore } from "@/store";
import { UserStore } from "@/store/constants";

let organizationId;
export const getOrganizationId = (): string | undefined => {
  const store = useStore();
  organizationId = store.getters[UserStore.Getters.ORGANIZATION_ID];
  return organizationId;
};

/**
 * Get all users in the current organization
 */
export const getAllUsers = async (): Promise<IUserResult[] | null> => {
  if (!organizationId) getOrganizationId();
  const users = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/users",
    "GET"
  );
  return users.users as IUserResult[];
};

/**
 * Gets user data by id
 * @param userId the id of the specified user
 */
export const getUser = async (userId: string): Promise<IUserResult | null> => {
  if (!organizationId) getOrganizationId();
  const user = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/users/" +
      userId,
    "GET"
  );
  return user as IUserResult;
};

/**
 * Invite a user by email address
 * @param email of the user
 */
export const inviteUser = async (email: string) => {
  if (!organizationId) return;
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/users",
    "POST",
    {},
    {
      email,
      name: "",
    }
  );
};

/**
 * Gets all groups by user
 * @param userId of the user
 */
export const getGroupsByUser = async (userId: string) => {
  if (!organizationId) return;
  const response = await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/users/" +
      userId +
      "/groups",
    "GET",
    {}
  );
  return response.groups as IGroupResult[];
};

/**
 * Updates a users permissions. Use this function only for user based permissions.
 * @param userId the user id
 * @param permissions permissions as string array to update
 */
export const updateUser = async (userId: string, permissions: string[]) => {
  if (!organizationId) return;
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/users/" +
      userId,
    "PUT",
    {},
    {
      permissions,
    }
  );
};

/**
 * Delete a user
 * @param userId the id of the user
 */
export const deleteUser = async (userId: string) => {
  if (!organizationId) return;
  await fetchCloudRun(
    process.env.VUE_APP_BASE_URL +
      "/admin/organizations/" +
      organizationId +
      "/users/" +
      userId,
    "DELETE",
    {
      force_delete: true,
    }
  );
};
